import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, Plus, Trophy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

type Story = {
  id: string;
  user_id: string;
  title: string;
  story: string;
  product_url: string | null;
  revenue_range: string | null;
  status: string;
  created_at: string;
};

const REVENUE_RANGES = ["< $1k", "$1k – $5k", "$5k – $25k", "$25k – $100k", "$100k+"];

const storySchema = z.object({
  title: z.string().trim().min(3).max(120),
  story: z.string().trim().min(20).max(2000),
  product_url: z.string().trim().url().max(500).or(z.literal("")),
  revenue_range: z.string().max(40),
});

export function SuccessStories() {
  const { user } = useAuth();
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    // RLS returns: own stories (any status) + approved (public to licensed users)
    const { data } = await supabase
      .from("success_stories")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(20);
    setStories((data as Story[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <section className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="font-mono text-xs text-primary mb-1 flex items-center gap-2">
            <Trophy className="size-3" /> COMMUNITY WINS
          </div>
          <h2 className="font-display text-2xl font-bold">Success stories from creators</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Real launches built from Trends Discovery insights. Yours next?
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="hero" size="sm">
              <Plus className="size-4" /> Share your win
            </Button>
          </DialogTrigger>
          <SubmitDialog
            userId={user?.id ?? null}
            onSubmitted={() => { setOpen(false); load(); }}
          />
        </Dialog>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="h-40 rounded-xl border border-border bg-card animate-pulse" />
          ))}
        </div>
      ) : stories.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No stories yet. Be the first — share what you launched.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {stories.map((s) => (
            <article key={s.id} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 mb-2">
                {s.user_id === user?.id && s.status !== "approved" && (
                  <span className="font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border border-border text-muted-foreground">
                    {s.status}
                  </span>
                )}
                {s.revenue_range && (
                  <span className="font-mono text-[10px] uppercase rounded px-1.5 py-0.5 border border-primary/40 text-primary">
                    {s.revenue_range}
                  </span>
                )}
              </div>
              <h3 className="font-display text-lg font-semibold">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{s.story}</p>
              {s.product_url && (
                <a
                  href={s.product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline"
                >
                  View product <ExternalLink className="size-3" />
                </a>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function SubmitDialog({ userId, onSubmitted }: { userId: string | null; onSubmitted: () => void }) {
  const [title, setTitle] = useState("");
  const [story, setStory] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [revenueRange, setRevenueRange] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!userId) return toast.error("Sign in to share a story");
    const parsed = storySchema.safeParse({
      title, story, product_url: productUrl, revenue_range: revenueRange,
    });
    if (!parsed.success) {
      const first = Object.values(parsed.error.flatten().fieldErrors)[0]?.[0];
      return toast.error(first ?? "Please check your inputs");
    }
    setSaving(true);
    const { error } = await supabase.from("success_stories").insert({
      user_id: userId,
      title: parsed.data.title,
      story: parsed.data.story,
      product_url: parsed.data.product_url || null,
      revenue_range: parsed.data.revenue_range || null,
    });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Submitted! We'll review and publish shortly.");
    setTitle(""); setStory(""); setProductUrl(""); setRevenueRange("");
    onSubmitted();
  };

  return (
    <DialogContent className="max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle>Share your success story</DialogTitle>
        <DialogDescription>
          Tell other creators what you launched and how Trends Discovery helped. Approved stories
          appear in the community feed.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <label className="font-mono text-xs text-primary block mb-1.5">Headline</label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. $8.4k in 3 weeks with an AI-CFO Notion template" maxLength={120} />
        </div>
        <div>
          <label className="font-mono text-xs text-primary block mb-1.5">Your story</label>
          <Textarea value={story} onChange={(e) => setStory(e.target.value)}
            placeholder="What was the trend? What did you build? What worked?"
            className="min-h-32" maxLength={2000} />
          <div className="text-[10px] font-mono text-muted-foreground text-right mt-1">
            {story.length}/2000
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="font-mono text-xs text-primary block mb-1.5">Product URL (optional)</label>
            <Input value={productUrl} onChange={(e) => setProductUrl(e.target.value)}
              placeholder="https://…" type="url" maxLength={500} />
          </div>
          <div>
            <label className="font-mono text-xs text-primary block mb-1.5">Revenue range</label>
            <div className="flex flex-wrap gap-1.5">
              {REVENUE_RANGES.map((r) => (
                <button key={r} type="button" onClick={() => setRevenueRange(r === revenueRange ? "" : r)}
                  className={`text-xs rounded-full border px-2.5 py-1 transition ${
                    r === revenueRange
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  }`}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="hero" onClick={submit} disabled={saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : null}
          Submit for review
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
